
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config/config';
import ShopPage from '../components/ShopPage';

const ShopPageAdmin = () => {
  const [design, setDesign] = useState('design1');
  const [title, setTitle] = useState('Shop All Products');
  const [subtitle, setSubtitle] = useState('Discover our complete collection');
  const [enableFilters, setEnableFilters] = useState(true);
  const [showViewToggle, setShowViewToggle] = useState(true);
  const [defaultSort, setDefaultSort] = useState('featured');
  const [maxPrice, setMaxPrice] = useState(500);
  const [devMode, setDevMode] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("token");


  const sampleProducts = [
    { id: 1, name: "Classic T-Shirt", price: 29.99, originalPrice: 39.99, category: "Clothing", rating: 4.5, reviews: 128, image: "bg-blue-100", badge: "Sale", sizes: ['S','M','L'], colors: ['Blue','Black'] },
    { id: 2, name: "Slim Jeans", price: 79.99, category: "Clothing", rating: 4.8, reviews: 89, image: "bg-indigo-100", badge: "Popular", sizes: ['28','30','32'], colors: ['Blue'] },
    
  ];

  const currentConfig = {
    design,
    title,
    subtitle,
    enableFilters,
    showViewToggle,
    defaultSort,
    maxPrice,
    products: sampleProducts,
    customStyles: devMode ? { backgroundColor } : {},
  };

  useEffect(() => {
    const fetchSettings = async () => {
      if (!token) return setLoading(false);
      try {
         await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
         const res = await axios.get(config.API_URL + '/api/shop-page-settings', {
           headers: { Authorization: `Bearer ${token}` },
           withCredentials: true,
         });
         const data = res.data.settings || {};
         setDesign(data.design || 'design1');
         setTitle(data.title || 'Shop All Products');
      
      } catch (err) {
        console.error("Failed to load shop page settings", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
       await axios.post(config.API_URL + '/api/shop-page-settings', { settings: currentConfig }, {
         headers: { Authorization: `Bearer ${token}` },
         withCredentials: true,
       });
      alert("Settings saved successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading settings...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Controls */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow border p-6">
            <h2 className="text-2xl font-semibold mb-6">Shop Page Editor</h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Layout</label>
                <select
                  value={design}
                  onChange={e => setDesign(e.target.value)}
                  className="w-full border rounded-lg p-3"
                >
                  <option value="design1">Sidebar Filters</option>
                  <option value="design2">Top Filters Bar</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Page Title</label>
                  <input
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    className="w-full border rounded-lg p-3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Subtitle</label>
                  <input
                    value={subtitle}
                    onChange={e => setSubtitle(e.target.value)}
                    className="w-full border rounded-lg p-3"
                  />
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">Enable Filters</span>
                  <button
                    onClick={() => setEnableFilters(!enableFilters)}
                    className={`w-12 h-6 rounded-full transition-colors ${enableFilters ? 'bg-blue-600' : 'bg-gray-300'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transform transition ${enableFilters ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-700">Show View Toggle (Grid/List)</span>
                  <button
                    onClick={() => setShowViewToggle(!showViewToggle)}
                    className={`w-12 h-6 rounded-full transition-colors ${showViewToggle ? 'bg-blue-600' : 'bg-gray-300'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transform transition ${showViewToggle ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-700">Default Sort</span>
                  <select
                    value={defaultSort}
                    onChange={e => setDefaultSort(e.target.value)}
                    className="border rounded-lg px-3 py-2"
                  >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="newest">Newest</option>
                    <option value="rating">Best Rating</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Max Price in Filter</label>
                <input
                  type="number"
                  value={maxPrice}
                  onChange={e => setMaxPrice(Number(e.target.value))}
                  className="w-full border rounded-lg p-3"
                  min="100"
                  step="50"
                />
              </div>

              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-700 font-medium">Developer Mode</span>
                  <button
                    onClick={() => setDevMode(!devMode)}
                    className={`w-12 h-6 rounded-full transition-colors ${devMode ? 'bg-yellow-500' : 'bg-gray-300'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transform transition ${devMode ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>

                {devMode && (
                  <input
                    type="text"
                    placeholder="Background color or gradient"
                    value={backgroundColor}
                    onChange={e => setBackgroundColor(e.target.value)}
                    className="w-full border rounded-lg p-3 mt-2"
                  />
                )}
              </div>
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
          >
            {saving ? 'Saving...' : 'Save & Publish Changes'}
          </button>
        </div>

        {/* Live Preview */}
        <div className="sticky top-6">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
            <div className="bg-gray-100 p-4 border-b">
              <h3 className="font-semibold text-lg">Live Preview</h3>
            </div>
            <div className={devMode && backgroundColor ? '' : 'bg-gradient-to-b from-sky-50 to-gray-100 min-h-[80vh]'}>
              <div style={devMode && backgroundColor ? { background: backgroundColor } : {}}>
                <ShopPage config={currentConfig} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopPageAdmin;